import type { OfflineLedger, OfflineLedgerEntry } from './offline-queue'
import {
  isOfflineRecordRevision,
  isOwnedOfflineEnvelope,
  isUnconfirmedLegacyWrite,
  OFFLINE_QUEUE_MAX_AGE_MS,
  OFFLINE_TASK_WRITE_COMMITTED_MESSAGE,
  selectOwnerWrites,
} from './offline-queue'

export function needsOfflineDecision(entry: OfflineLedgerEntry): boolean {
  return (
    ['failed', 'conflicted', 'needs-review', 'expired'].includes(
      entry.status,
    ) || isUnconfirmedLegacyWrite(entry)
  )
}

export function canRetryOfflineWrite(
  entry: OfflineLedgerEntry,
  now = Date.now(),
): boolean {
  return (
    needsOfflineDecision(entry) &&
    (entry.envelope.kind !== 'task.status' ||
      isOfflineRecordRevision(entry.envelope.recordRevision)) &&
    entry.status !== 'expired' &&
    entry.status !== 'conflicted' &&
    now - entry.envelope.enqueuedAt <= OFFLINE_QUEUE_MAX_AGE_MS
  )
}

export function selectUnsentWorkspaceWrites(
  entries: OfflineLedgerEntry[],
  ownerId: string | null,
  organizationId: string | null,
): OfflineLedgerEntry[] {
  if (!organizationId) return []
  return selectOwnerWrites(entries, ownerId).filter(
    entry =>
      entry.envelope.organizationId === organizationId &&
      entry.status === 'queued' &&
      entry.attempts === 0,
  )
}

export function nonRetryableOfflineWriteMessage(
  entry: OfflineLedgerEntry,
): string {
  if (
    entry.status === 'conflicted' &&
    entry.message === OFFLINE_TASK_WRITE_COMMITTED_MESSAGE
  )
    return OFFLINE_TASK_WRITE_COMMITTED_MESSAGE
  return entry.status === 'conflicted' ||
    (entry.envelope.kind === 'task.status' &&
      !isOfflineRecordRevision(entry.envelope.recordRevision))
    ? 'Reload the task and check its current status. Discard this saved change and make a new change if it is still needed.'
    : 'This change is too old to send safely. Discard it and create a new change.'
}

export interface RecoveryOutcome {
  status:
    | 'completed'
    | 'cancelled'
    | 'changed'
    | 'workspace'
    | 'offline'
    | 'expired'
    | 'conflicted'
    | 'unconfirmed'
  message?: string
}

/** Recovery controls own the displayed scope and exact ledger row, before mutation. */
export function createOfflineRecoveryActions(options: {
  session: { assertCurrent(): void; confirmCurrent?(): Promise<void> }
  ownerId: string | null
  organizationId: string | null
  ledger: OfflineLedger
  isOnline(): boolean
  retry(entry: OfflineLedgerEntry): Promise<void>
  discard(entry: OfflineLedgerEntry): void
  flush(): Promise<void>
}) {
  function isCurrent() {
    try {
      options.session.assertCurrent()
      return true
    } catch {
      return false
    }
  }
  function check(entry: OfflineLedgerEntry): RecoveryOutcome | undefined {
    if (!isCurrent()) return { status: 'cancelled' }
    if (
      !options.ownerId ||
      !isOwnedOfflineEnvelope(entry.envelope) ||
      entry.envelope.ownerId !== options.ownerId ||
      options.ledger.get(entry.envelope.idempotencyKey, options.ownerId) !==
        entry ||
      !needsOfflineDecision(entry)
    )
      return {
        status: 'changed',
        message:
          'This change has moved on. Check its current status before trying again.',
      }
  }
  async function finish(
    entry: OfflineLedgerEntry,
    run: () => void | Promise<void>,
    validate?: () => RecoveryOutcome | undefined,
  ): Promise<RecoveryOutcome> {
    try {
      await options.session.confirmCurrent?.()
      // A native credential read yields; never reset/remove a row that moved on.
      const invalid = check(entry) ?? validate?.()
      if (invalid) return invalid
      await run()
      await options.flush()
      return isCurrent() ? { status: 'completed' } : { status: 'cancelled' }
    } catch {
      return isCurrent()
        ? {
            status: 'unconfirmed',
            message:
              'Could not confirm the recovery action. Reload the app before trying again.',
          }
        : { status: 'cancelled' }
    }
  }
  return {
    async retry(entry: OfflineLedgerEntry): Promise<RecoveryOutcome> {
      const invalid = check(entry)
      if (invalid) return invalid
      const validate = (): RecoveryOutcome | undefined => {
        if (!canRetryOfflineWrite(entry))
          return {
            status:
              entry.status === 'conflicted' ||
              (entry.envelope.kind === 'task.status' &&
                !isOfflineRecordRevision(entry.envelope.recordRevision))
                ? 'conflicted'
                : 'expired',
            message: nonRetryableOfflineWriteMessage(entry),
          }
        if (entry.envelope.organizationId !== options.organizationId)
          return {
            status: 'workspace',
            message:
              'Switch to the original workspace to send this change again.',
          }
        if (!options.isOnline())
          return {
            status: 'offline',
            message: 'Connect to the internet to send this change again.',
          }
      }
      const unavailable = validate()
      if (unavailable) return unavailable
      return finish(entry, () => options.retry(entry), validate)
    },
    async discard(entry: OfflineLedgerEntry): Promise<RecoveryOutcome> {
      const invalid = check(entry)
      if (invalid) return invalid
      return finish(entry, () => options.discard(entry))
    },
  }
}
